import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { ApiResponse } from '../../../../shared/dtos/api-response';
import { ApiError } from '../../../../shared/dtos/api-error';
import { PaginatedResult } from '../../../../shared/dtos/paginated-result';
import { ApplicationMapper } from '../models/application';
import type { Application } from '../models/application';
import { ApplicationsStore } from '../stores/applications.store';
import {
  INTERNAL_APPLICATIONS_ENDPOINT,
  INTERNAL_APPLICATIONS_PAGED_ENDPOINT,
} from '../constants/endpoints';
import type { ApplicationSummary } from '../dtos/application';

export interface ApplicationsPageQuery {
  page: number;
  limit: number;
  filter?: string;
}

@Injectable({ providedIn: 'root' })
export class ApplicationsService {
  private readonly http = inject(HttpClient);
  private readonly applicationsStore = inject(ApplicationsStore);

  /** Stub: loads applications when backend list endpoint is available */
  getApplications(): Observable<Application[]> {
    this.applicationsStore.setLoading();
    return this.http.get<ApiResponse<ApplicationSummary[]>>(INTERNAL_APPLICATIONS_ENDPOINT).pipe(
      map((resp) => ApplicationMapper.fromDtoList(resp.data)),
      tap((records) => this.applicationsStore.setRecords(records)),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.applicationsStore.setRecords([]);
          return of([]);
        }
        const body = err.error as ApiError | undefined;
        this.applicationsStore.setError(body?.message ?? 'Failed to load applications.');
        return throwError(() => body ?? { message: 'Failed to load applications.' });
      }),
    );
  }

  /** Get a page of applications */
  getApplicationsPaged(query: ApplicationsPageQuery): Observable<PaginatedResult<Application>> {
    let params = new HttpParams().set('page', String(query.page)).set('limit', String(query.limit));

    const filter = query.filter?.trim();
    if (filter) {
      params = params.set('filter', filter);
    }

    return this.http
      .get<ApiResponse<PaginatedResult<ApplicationSummary>>>(INTERNAL_APPLICATIONS_PAGED_ENDPOINT, {
        params,
      })
      .pipe(
        map((resp) => ({
          items: ApplicationMapper.fromDtoList(resp.data.items),
          totalCount: resp.data.totalCount,
          page: resp.data.page,
          limit: resp.data.limit,
        })),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to load applications.' });
        }),
      );
  }
}
